import {
  PIRATES,
  findPath,
  hexDistance,
  hexRing,
  type FleetState,
  type GameState,
  type Hex,
  type ShipState,
} from '@sd/sim';
import { SHIP_COMBAT, type ShipClassId } from '@sd/content';

/**
 * The lobby's backdrop (#98, the author's request of 21.10.2026): no map of
 * the coming match — its system is the player's to discover — but a mass
 * battle on a fixed map, over and over. Fleets of two sides fly in, fight
 * over one hex while debris piles up, one side falls back, and it starts
 * again. A state for the live map, made up tick by tick; no match runs.
 */
export const LOBBY_SEED = 'lobby-battle';
/** One round of the scene, in ticks (10 a second). */
const ROUND = 700;
const APPROACH = 220;
const FIGHT = 480;

/** Along the battle line: the two sides face each other across `center` (#141). */
const FRONT: Hex = { q: 1, r: 0 };
/** A second lane beside the first, for a side's extra squadron. */
const LANE: Hex = { q: 0, r: 1 };
const at = (c: Hex, d: Hex, k: number, lane = 0): Hex => ({
  q: c.q + d.q * k + LANE.q * lane,
  r: c.r + d.r * k + LANE.r * lane,
});

/**
 * Where each squadron of a side stands to fight, as steps along its front from the
 * centre (#141, ranged combat of #117): corvettes close in to 1 hex of the enemy's,
 * frigates hold 2 behind, cruisers 3 — each fires on the nearest enemy in its range.
 */
const LINE: readonly {
  readonly ships: readonly ShipClassId[];
  readonly step: number;
  readonly lane: number;
}[][] = [
  [
    { ships: ['corvette', 'corvette', 'corvette'], step: 0, lane: 0 },
    { ships: ['frigate', 'frigate', 'frigate'], step: -1, lane: 0 },
    { ships: ['cruiser', 'cruiser'], step: -2, lane: 0 },
    { ships: ['corvette', 'corvette'], step: 0, lane: -1 },
  ],
  [
    { ships: ['raider', 'raider', 'raider'], step: 1, lane: 0 },
    { ships: ['frigate', 'frigate', 'frigate'], step: 2, lane: 0 },
    { ships: ['cruiser', 'cruiser'], step: 3, lane: 0 },
    { ships: ['corvette', 'corvette'], step: 1, lane: 1 },
  ],
];

/** Every hex the scene stands on: open space, away from bodies. */
const lineHexes = (c: Hex) => LINE.flatMap((side) => side.map((f) => at(c, FRONT, f.step, f.lane)));

/** The hex the fight is over: open space near the middle of the map, away from bodies. */
export function battleHex(base: GameState): Hex {
  const bodies = base.system.planets.map((p) => p.hex);
  const free = (h: Hex) => bodies.every((b) => hexDistance(b, h) >= 2);
  const candidates = hexRing({ q: 0, r: 0 }, Math.round(base.system.mapRadius * 0.55));
  return candidates.find((c) => lineHexes(c).every(free)) ?? candidates[0]!;
}

const fleet = (
  id: number,
  owner: string,
  hex: Hex,
  path: readonly Hex[],
  progress: number,
  classes: readonly ShipClassId[],
): FleetState => ({
  id,
  owner,
  hex,
  path: [...path],
  progress,
  scan: null,
  ships: classes.map((cls, k): ShipState => ({ id: id * 100 + k, cls })),
});

/** Where a fleet stands `step` hexes along `path` from `from`, and what is left of its way. */
function along(from: Hex, path: readonly Hex[], step: number): { hex: Hex; rest: Hex[] } {
  const done = Math.max(0, Math.min(path.length, step));
  return { hex: done === 0 ? from : path[done - 1]!, rest: path.slice(done) };
}

export function lobbyBattleState(base: GameState, tick: number): GameState {
  const center = battleHex(base);
  const t = tick % ROUND;
  const round = Math.floor(tick / ROUND);
  const owners = [base.players[0]!.id, base.players[1]?.id ?? PIRATES];
  const fleets: FleetState[] = [];
  // The losing side changes every round, so the scene never looks the same twice.
  const loser = round % 2;
  LINE.forEach((side, s) => {
    const owner = owners[s]!;
    // Each side comes in from its own end of the front, six hexes out.
    const away = s === 0 ? -6 : 7;
    side.forEach((squadron, f) => {
      const goal = at(center, FRONT, squadron.step, squadron.lane);
      const from = at(center, FRONT, away + squadron.step, squadron.lane);
      const path = findPath(base.system, from, goal) ?? [goal];
      const id = 1000 + s * 10 + f;
      const ships = squadron.ships;
      if (t < APPROACH) {
        // Flying in, the back rows a little later: they stop short of the front.
        const per = APPROACH / Math.max(1, path.length);
        const where = along(from, path, Math.floor(t / per));
        fleets.push(fleet(id, owner, where.hex, where.rest, Math.floor(t % per), ships));
      } else if (t < FIGHT) {
        // The fight at range: the losing side's ships fall one by one.
        const lost =
          s === loser ? Math.floor(((t - APPROACH) / (FIGHT - APPROACH)) * ships.length) : 0;
        const left = ships.slice(0, Math.max(1, ships.length - lost));
        fleets.push(fleet(id, owner, goal, [], 0, left));
      } else if (s !== loser) {
        fleets.push(fleet(id, owner, goal, [], 0, ships));
      } else {
        // Falling back the way it came.
        const back = findPath(base.system, goal, from) ?? [from];
        const per = (ROUND - FIGHT) / Math.max(1, back.length);
        const where = along(goal, back, Math.floor((t - FIGHT) / per));
        fleets.push(
          fleet(id, owner, where.hex, where.rest, Math.floor((t - FIGHT) % per), ships.slice(0, 1)),
        );
      }
    });
  });
  // Wrecks pile up where the lines meet.
  const wrecks = [
    at(center, FRONT, 0),
    at(center, FRONT, 1),
    at(center, FRONT, 0, -1),
    at(center, FRONT, 1, 1),
  ];
  return {
    ...base,
    tick,
    fleets,
    // Fire from afar pins nobody (#117): no melee in the scene.
    battles: [],
    debris:
      t >= APPROACH + 60
        ? wrecks.map((hex, i) => ({ hex, metals: 20 + ((t + i * 37) % 120) }))
        : [],
  };
}

/**
 * The lobby's fire (#117): every second, each ship fires at the nearest
 * enemy fleet within its range — the same bolts as a match draws.
 */
export function lobbyVolley(
  state: GameState,
): { from: Hex; to: Hex; owner: string; cls: ShipClassId }[] {
  if (state.tick % 10 !== 0) return [];
  const shots: { from: Hex; to: Hex; owner: string; cls: ShipClassId }[] = [];
  for (const a of state.fleets) {
    for (const cls of new Set(a.ships.map((s) => s.cls))) {
      const range = SHIP_COMBAT[cls].range;
      const target = state.fleets
        .filter((b) => b.owner !== a.owner && hexDistance(a.hex, b.hex) <= range)
        .sort((x, y) => hexDistance(a.hex, x.hex) - hexDistance(a.hex, y.hex))[0];
      if (target) shots.push({ from: a.hex, to: target.hex, owner: a.owner, cls });
    }
  }
  return shots;
}
